import React from 'react';
import { Video, Settings, AlertTriangle, CheckCircle, ExternalLink, HelpCircle } from 'lucide-react';

const ZoomDocumentation = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <h1 className="text-3xl font-bold text-blue-600 border-b-2 border-blue-600 pb-4 mb-6">
            Zoom Integration Guide
          </h1>
          
          <p className="text-lg text-gray-700 mb-8">
            This guide will walk you through connecting Zoom to your Athena account, using Zoom features, and troubleshooting common issues.
          </p>

          {/* Table of Contents */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-blue-800 mb-4">Table of Contents</h3>
            <ul className="space-y-2">
              <li><a href="#adding-zoom" className="text-blue-600 hover:underline">Adding Zoom Integration</a></li>
              <li><a href="#using-zoom" className="text-blue-600 hover:underline">Using Zoom Features</a></li>
              <li><a href="#troubleshooting" className="text-blue-600 hover:underline">Troubleshooting</a></li>
              <li><a href="#removing-zoom" className="text-blue-600 hover:underline">Removing Zoom Integration</a></li>
              <li><a href="#support" className="text-blue-600 hover:underline">Getting Help</a></li>
            </ul>
          </div>

          {/* Adding Zoom Integration */}
          <section id="adding-zoom" className="mb-12">
            <h2 className="text-2xl font-semibold text-blue-800 mb-6 flex items-center">
              <Video className="w-6 h-6 mr-2" />
              Adding Zoom Integration
            </h2>

            <div className="space-y-6">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Step 1: Navigate to Integrations</h3>
                <ol className="list-decimal pl-6 space-y-2">
                  <li>Log into your Athena account</li>
                  <li>Click on your profile icon in the top-right corner</li>
                  <li>Select "Settings" from the dropdown menu</li>
                  <li>Click on the "Personal Integrations" tab</li>
                </ol>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Step 2: Connect Zoom</h3>
                <ol className="list-decimal pl-6 space-y-2">
                  <li>Find the Zoom integration card</li>
                  <li>Click the "Connect" button</li>
                  <li>You'll be redirected to Zoom's authorization page</li>
                  <li>Sign in to your Zoom account if prompted</li>
                  <li>Review the permissions Athena is requesting</li>
                  <li>Click "Authorize" to grant permissions</li>
                </ol>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
                <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-green-800 font-semibold">Success!</p>
                  <p className="text-green-700">Once authorized, you'll be redirected back to Athena and your Zoom integration will be active.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Using Zoom Features */}
          <section id="using-zoom" className="mb-12">
            <h2 className="text-2xl font-semibold text-blue-800 mb-6 flex items-center">
              <Settings className="w-6 h-6 mr-2" />
              Using Zoom Features
            </h2>

            <div className="space-y-6">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Meeting Creation</h3>
                <p className="text-gray-700 mb-4">
                  With Zoom integrated, you can:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Create Zoom meetings directly from Athena</li>
                  <li>Schedule meetings with automatic Zoom links</li>
                  <li>Send meeting invitations to clients and team members</li>
                  <li>Access meeting recordings and transcripts</li>
                </ul>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Calendar Integration</h3>
                <p className="text-gray-700 mb-4">
                  Your Zoom meetings will automatically:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Appear in your Athena calendar</li>
                  <li>Include Zoom join links</li>
                  <li>Send notifications before meetings start</li>
                  <li>Track meeting attendance and duration</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Troubleshooting */}
          <section id="troubleshooting" className="mb-12">
            <h2 className="text-2xl font-semibold text-blue-800 mb-6 flex items-center">
              <AlertTriangle className="w-6 h-6 mr-2" />
              Troubleshooting
            </h2>

            <div className="space-y-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-yellow-800 mb-4">Connection Failed</h3>
                <p className="text-yellow-700 mb-4">If you're unable to connect your Zoom account:</p>
                <ul className="list-disc pl-6 space-y-2 text-yellow-700">
                  <li>Ensure you're using a Zoom Pro, Business, or Enterprise account</li>
                  <li>Check that your Zoom account has admin privileges</li>
                  <li>Try connecting in an incognito/private browser window</li>
                  <li>Clear your browser cache and cookies</li>
                  <li>Disable browser extensions that might interfere</li>
                </ul>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-red-800 mb-4">Authorization Errors</h3>
                <p className="text-red-700 mb-4">If you see authorization errors:</p>
                <ul className="list-disc pl-6 space-y-2 text-red-700">
                  <li>Make sure you're signed into the correct Zoom account</li>
                  <li>Verify your organization allows third-party app integrations</li>
                  <li>Contact your Zoom administrator if you're part of a managed account</li>
                  <li>Try disconnecting and reconnecting the integration</li>
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-800 mb-4">Meeting Issues</h3>
                <p className="text-blue-700 mb-4">If meetings aren't creating properly:</p>
                <ul className="list-disc pl-6 space-y-2 text-blue-700">
                  <li>Check that your Zoom integration is still connected</li>
                  <li>Verify your Zoom account has meeting creation permissions</li>
                  <li>Ensure your Zoom account hasn't exceeded meeting limits</li>
                  <li>Try refreshing your browser and attempting again</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Removing Integration */}
          <section id="removing-zoom" className="mb-12">
            <h2 className="text-2xl font-semibold text-blue-800 mb-6">Removing Zoom Integration</h2>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <p className="text-gray-700 mb-4">To disconnect your Zoom account:</p>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Go to Settings → Personal Integrations</li>
                <li>Find the Zoom integration</li>
                <li>Click "Disconnect" or "Remove"</li>
                <li>Confirm the removal when prompted</li>
              </ol>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4 flex items-start">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-yellow-800 font-semibold">Note:</p>
                  <p className="text-yellow-700">Removing the integration will not delete existing meetings but will prevent creating new ones through Athena.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Getting Help */}
          <section id="support" className="mb-8">
            <h2 className="text-2xl font-semibold text-blue-800 mb-6 flex items-center">
              <HelpCircle className="w-6 h-6 mr-2" />
              Getting Help
            </h2>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <p className="text-gray-700 mb-4">
                Still having trouble with your Zoom integration? We're here to help!
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <span className="font-semibold text-gray-800 mr-2">Email Support:</span>
                  <a href="mailto:support@rclintegrated.com" className="text-blue-600 hover:underline">
                    support@rclintegrated.com
                  </a>
                </div>
                <div className="flex items-center">
                  <span className="font-semibold text-gray-800 mr-2">General Support:</span>
                  <a href="/support" className="text-blue-600 hover:underline flex items-center">
                    Visit our Support page
                    <ExternalLink className="w-4 h-4 ml-1" />
                  </a>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-4 italic">
                When contacting support about Zoom issues, please include your account email and describe the specific problem you're experiencing.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ZoomDocumentation;